import { createFileRoute, Link } from '@tanstack/react-router'
import { type FormEvent, useState } from 'react'
import { AuthCard } from '@/components/auth-card'
import { Footer } from '@/components/footer'
import { NavBar } from '@/components/nav-bar'
import { TextInput } from '@/components/text-input'
import { PlatformApiError, requestPlatformPasswordReset } from '@/lib/api'
import { friendlyErrorMessage } from '@/lib/errors'
import { cn } from '@/lib/utils'

/**
 * Forgot-password page. Two states:
 *
 *   1. **Form**: user enters their email, we POST to the backend.
 *   2. **Sent**: generic confirmation copy that does NOT distinguish
 *      "email exists → reset sent" from "email doesn't exist →
 *      nothing sent". This is deliberate — otherwise the form
 *      becomes an account-enumeration oracle.
 *
 * The only error we surface specifically is 429 (rate limiting),
 * because that's actionable for the user ("try again later"). All
 * other errors fall through to a generic "something went wrong"
 * message so the page doesn't leak whether the backend reached
 * the user record.
 *
 * After the user clicks the link in the email:
 *   - better-auth validates the token server-side
 *   - redirects to `${redirectTo}?token=xyz` where redirectTo is
 *     the `/reset-password` URL we pass below
 *   - that's the companion page in this folder
 */
export const Route = createFileRoute('/forgot-password')({
  component: ForgotPasswordPage,
})

function ForgotPasswordPage() {
  const [email, setEmail] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    setError(null)
    setSubmitting(true)
    try {
      // redirectTo is the public URL of our reset page. better-auth
      // will embed this in the link it emails, then redirect to
      // `${redirectTo}?token=xyz` after the user clicks.
      const redirectTo = `${window.location.origin}/reset-password`
      await requestPlatformPasswordReset({ email, redirectTo })
      setSubmitted(true)
    } catch (err) {
      // 429 rate-limit: descriptive message from the classifier.
      // 5xx / network: "on our side, will be fixed" copy.
      // Other 4xx: intentionally vague since we don't want to
      // leak whether the email exists or not (enumeration oracle).
      if (
        err instanceof PlatformApiError &&
        err.status >= 400 &&
        err.status < 500 &&
        err.status !== 429
      ) {
        setError('Something went wrong. Try again in a moment.')
      } else {
        setError(friendlyErrorMessage(err))
      }
    } finally {
      setSubmitting(false)
    }
  }

  if (submitted) {
    return (
      <>
        <NavBar />
        <main>
          <AuthCard
            title="Check your email"
            subtitle={`If an account exists for ${email}, we've sent a password reset link.`}
            footer={
              <span>
                Didn't get an email? Check your spam folder or{' '}
                <button
                  type="button"
                  onClick={() => {
                    setSubmitted(false)
                    setError(null)
                  }}
                  className="font-medium text-primary hover:underline"
                >
                  try a different address
                </button>
                .
              </span>
            }
          >
            <p className="text-sm text-muted-foreground">
              Follow the link in the email to choose a new password. The link expires in 60 minutes
              — request a fresh one if it runs out.
            </p>
            <div className="mt-6">
              <Link to="/sign-in" className="text-sm font-medium text-primary hover:underline">
                Back to sign in
              </Link>
            </div>
          </AuthCard>
        </main>
        <Footer />
      </>
    )
  }

  return (
    <>
      <NavBar />
      <main>
        <AuthCard
          title="Reset your password"
          subtitle="Enter your email and we'll send you a link to choose a new password."
          footer={
            <span>
              Remembered it?{' '}
              <Link to="/sign-in" className="font-medium text-primary hover:underline">
                Back to sign in
              </Link>
            </span>
          }
        >
          <form onSubmit={handleSubmit} className="space-y-4" noValidate>
            <TextInput
              label="Email"
              type="email"
              autoComplete="email"
              required
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.currentTarget.value)}
            />

            {error ? (
              <div className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive">
                {error}
              </div>
            ) : null}

            <button
              type="submit"
              disabled={submitting || !email}
              className={cn(
                'w-full rounded-lg px-4 py-2.5 text-sm font-medium',
                'bg-primary text-primary-foreground shadow-md shadow-primary/20',
                'hover:opacity-90 transition-opacity',
                'disabled:opacity-60 disabled:cursor-not-allowed',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
              )}
            >
              {submitting ? 'Sending…' : 'Send reset link'}
            </button>
          </form>
        </AuthCard>
      </main>
      <Footer />
    </>
  )
}
